import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
	Dropdown,
	DropdownToggle,
	DropdownMenu,
	DropdownItem
} from 'reactstrap';

import { resizeImage } from '@techchange/filestack';
import { messages } from '../../../i18n';

import TemplateSignatureForm from './TemplateSignatureForm';

const propTypes = {

	/**
	 * The template signature.
	 */
	templateSignature: PropTypes.shape({

		/**
		 * The certificate person's ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The ID of the associated person.
		 */
		person_id: PropTypes.string.isRequired,

		/**
		 * The person's name.
		 */
		name: PropTypes.string.isRequired,

		/**
		 * The person's affiliation (i.e. occupation/role).
		 */
		affiliation: PropTypes.string.isRequired,

		/**
		 * The person's signature.
		 */
		signature: PropTypes.string,

	}),

	/**
	 * The list of all available people for this organization.
	 */
	people: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The person's ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The person's name.
		 */
		name: PropTypes.string.isRequired,

		/**
		 * The person's affiliation (i.e. occupation/role).
		 */
		affiliation: PropTypes.string.isRequired,

		/**
		 * The person's signature.
		 */
		signature: PropTypes.string,

	})).isRequired,

	/**
	 * The filestack credentials object.
	 */
	filestack: PropTypes.shape({

		apiKey: PropTypes.string.isRequired,

		security: PropTypes.shape({

			policy: PropTypes.string.isRequired,

			signature: PropTypes.string.isRequired,

		}).isRequired,

	}).isRequired,

	/**
	 * Callback function to edit the person for the template signature.
	 */
	onEdit: PropTypes.func.isRequired,

	/**
	 * Callback function to delete the template signature.
	 */
	onDelete: PropTypes.func.isRequired,

};

const TemplateSignature = ({ templateSignature, people, filestack, onEdit, onDelete }) => {

	const intl = useIntl();

	const [showActions, setShowActions] = useState(false);
	const [isEditing, setIsEditing] = useState(false);

	const handleOnEdit = (data) => {

		setIsEditing(false);
		setShowActions(false);

		onEdit(data, true);

	};

	return (
		<div className="template-signature">
			{!isEditing &&
				<div className="d-flex">
					<div className="template-signature-details">
						<div className="template-signature-img">
							{templateSignature.signature &&
								<img
									alt={intl.formatMessage(messages.TC_COMPONENTS_CERTIFICATE_EDITOR_PERSON_FIELD_LABEL_SIGNATURE)}
									src={resizeImage(templateSignature.signature, 'height', 50)} />
							}
						</div>
						<b>{templateSignature.name}</b>
						<div className="template-signature-affiliation">{templateSignature.affiliation}</div>
					</div>
					<Dropdown
						className="ms-auto"
						isOpen={showActions}
						toggle={() => setShowActions((prev) => !prev)}>
						<DropdownToggle
							tag="span"
							color="link">
							<span
								className="icon-btn"
								title={intl.formatMessage(messages.TC_COMPONENTS_GENERAL_MORE_LABEL)}>
								<i className="fas fa-ellipsis" />
							</span>
						</DropdownToggle>
						<DropdownMenu end>
							<DropdownItem onClick={() => setIsEditing((prev) => !prev)}>
								<span>
									<i className="fas fa-pencil" />
									{intl.formatMessage(messages.TC_COMPONENTS_GENERAL_EDIT_LABEL)}
								</span>
							</DropdownItem>
							<DropdownItem onClick={() => onDelete(templateSignature.id)}>
								<span>
									<i className="fas fa-trash" />
									{intl.formatMessage(messages.TC_COMPONENTS_GENERAL_DELETE_LABEL)}
								</span>
							</DropdownItem>
						</DropdownMenu>
					</Dropdown>
				</div>
			}
			{isEditing &&
				<TemplateSignatureForm
					initialValues={templateSignature}
					people={people}
					filestack={filestack}
					onAdd={handleOnEdit}
					onCancel={() => setIsEditing(false)}
					onDelete={onDelete} />
			}
		</div>
	);

};

TemplateSignature.propTypes = propTypes;

export default TemplateSignature;
